import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import * as Routes from '@/modules/routes';

/** Classes carry id and name together — two of a teacher's classes can share a name. */
export interface ClassRef {
  id: number;
  name: string | null;
}

export interface Served {
  course_id: string | null;
  classroom_id: number | null;
  serves?: string | null;
  votes?: number;
  of?: number;
  error?: boolean;
}

/** Recovered from the deep-link launch that created the assignment, for rows that never linked. */
export interface CreatedFrom {
  course_id: string | null;
  course_title: string | null;
  seconds_before: number;
  /** Other deep links inside the same window — nearest-preceding is a guess, not a certainty. */
  alternatives: number;
}

export interface MatchRow {
  assignment_id: number;
  title: string;
  classes: number;
  courses: number;
  serves: Served[];
  reaches: ClassRef[];
  stranded: ClassRef[];
  /** Classes none of its linked courses hold any student of — what the unassign picker offers. */
  courseless: ClassRef[];
  created_from?: CreatedFrom | null;
}

/** A teacher's class, placed in the LMS course most of its students sit in (inferred, not saved). */
export interface MapClass {
  id: number;
  name: string | null;
  students: number;
  never_launched: number;
  in_a_course: number;
  course_id: string | null;
  in_that_course: number;
}

export interface MatchPayload {
  rows?: MatchRow[];
  class_map?: { classes: MapClass[] };
  /** The LMS's own course titles — class names often cannot tell a teacher's courses apart. */
  course_titles?: Record<string, string | null>;
  courses_read?: number;
  recovered?: number;
  unreachable?: number;
  error?: string;
}

// AIDEV-NOTE: Lives outside the panel because the assignments table reads it too — the wiring belongs
// on the same row as the delivery health, not in a second table of the same assignments.
const useCourseMatch = (stafferId: string, enabled: boolean) => useQuery({
  enabled: enabled && stafferId.trim().length > 0,
  queryFn: async (): Promise<MatchPayload> => {
    const { data } = await axios.get(
      Routes.plato_api_sysadmin_grade_sync_course_match_path(),
      { params: { staffer_id: stafferId } },
    );

    return data.data;
  },
  queryKey: ['gradeSyncCourseMatch', stafferId],
  staleTime: 5 * 60 * 1000,
});

export default useCourseMatch;
